import React, { useState } from "react";
import { Drawer, useMediaQuery, List, ListSubheader, ListItem, ListItemIcon, ListItemText, Divider, Box, Typography, Button, Tooltip } from "@material-ui/core";
import { Event as EventIcon, ViewList as ViewListIcon, Group as GroupIcon, Home as HomeIcon, HelpOutline as HelpIcon, EmojiPeople as EmojiPeopleIcon, RemoveFromQueue as RemoveFromQueueIcon, ExitToApp as ExitToAppIcon, AccountCircle as AccountCircleIcon, Description as DescriptionIcon, GolfCourse as GolfCourseIcon, SportsTennis as SportsTennisIcon, EventAvailable as EventAvailableIcon, Settings as SettingsIcon, EmojiEventsOutlined as ChallengeIcon } from "@material-ui/icons";
import { View } from "features/global/reducer";
import { UserProfile } from "api/user";
import { patchExtendDemoMode } from "api/owner/settings";
import { Club, IncreasedDataProtections } from "api/clubs";
import { PersistedData, getPersistedData } from "utils/persist";
import { useSnackbar } from "notistack";
import { helpB2BLink, helpLink } from "utils/helpers";
import moment from "moment";
import styles from "./Menu.module.scss";
import i18n from "../i18n";
import LanguageSelect from "./LanguageSelect";
import EndDemoDialog from "./EndDemoDialog";
import Onboarding from "./Onboarding";

// prop types
interface MenuProps {
    club: Club | null,
    showMenu: boolean,
    userProfile: UserProfile | null,
    view: View,
    onFetchUserProfile: () => void,
    onLogoutDashboard: () => void,
    onNavigateTo: (view: View) => void,
    onSetClub: (club: Club) => void,
    onSignOut: () => void,
    onToggleMenu: () => void,
}

// menu section
const Section = ({ label, children }: { label: string, children?: React.ReactNode }) => (
    <>
        <List
            subheader={
                <ListSubheader disableSticky>{label}</ListSubheader>
            }
        >
            {children}
        </List>
        <Divider />
    </>
);

// app menu drawer
const Menu = ({ club, showMenu, userProfile, view: currentView, onFetchUserProfile, onLogoutDashboard, onNavigateTo, onSetClub, onSignOut, onToggleMenu }: MenuProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [showEndDemoDialog, setShowEndDemoDialog] = useState(false);

    // extend demo action
    const extendDemo = async () => {
        setLoading(true);
        try {
            const updatedClub = await patchExtendDemoMode({ clubId: club!.id });
            onSetClub(updatedClub);
            enqueueSnackbar(i18n.extendDemoSuccess, { variant: "success" });
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render navigation item
    const NavigationItem = ({ label, icon, view, onClick }: { label: string, icon: React.ReactNode, view?: View, onClick?: () => void }) => (
        <ListItem
            button
            selected={currentView === view}
            onClick={view ? () => onNavigateTo(view) : onClick}
        >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
        </ListItem>
    );

    // remaining days for demo clubs
    const demoDeadline = club && moment(club.demoModeEndDate);
    const daysRemaining = demoDeadline && Math.max(0, demoDeadline.diff(moment(), "days"));

    // render menu
    const isSmallScreen = useMediaQuery((theme: any) => theme.breakpoints.down("xs"));
    const userRole = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole;
    const isCompany = club?.capabilities.includes("company");
    const clubSetupIsCompleted = club?.clubSetupIsCompleted || club?.clubStatus === "demo"; // demo is ok
    return (
        <Drawer
            className={showMenu ? styles.drawer : styles.hideDrawer}
            classes={{ paper: styles.drawerPaper }}
            open={showMenu}
            variant={isSmallScreen ? "temporary" : "persistent"}
            onClose={onToggleMenu}
        >
            {/* demo club */}
            {club?.clubStatus === "demo" && (
                <>
                    <EndDemoDialog club={club} show={showEndDemoDialog} onClose={() => setShowEndDemoDialog(false)} onFetchUserProfile={onFetchUserProfile} onSetClub={onSetClub} />
                    <Box style={{ textAlign: "center", padding: "16px 0" }}>
                        <Typography variant="h6">{i18n.demo}</Typography>
                        <Typography>{i18n.daysRemaining(daysRemaining)}</Typography>
                        {userProfile!.onboardingStep !== 4 && (
                            <Tooltip title={userRole !== "owner" ? i18n.migrateNowOwnerOnly : ""}>
                                <Box>
                                    <Button disabled={userRole !== "owner"} style={{ marginTop: "8px" }} onClick={() => setShowEndDemoDialog(true)}>
                                        {i18n.migrateNow}
                                    </Button>
                                </Box>
                            </Tooltip>
                        )}
                        {daysRemaining! <= 5 && userRole === "owner" && (
                            <Button style={{ marginTop: "8px" }} onClick={extendDemo} disabled={isLoading}>
                                {i18n.extendDemo}
                            </Button>
                        )}
                    </Box>
                    <Divider />
                </>
            )}

            {/* onboarding (if verified or demo) */}
            {clubSetupIsCompleted && (
                <>
                    {userProfile && club && (
                        <Onboarding
                            club={club}
                            userProfile={userProfile}
                            onFetchUserProfile={onFetchUserProfile}
                            onNavigateTo={onNavigateTo as any}
                            onShowEndDemoDialog={() => setShowEndDemoDialog(true)}
                        />
                    )}
                    <Divider />
                </>
            )}

            {/* dashboard section */}
            <Section label={i18n.dashboard}>
                <NavigationItem label={i18n.home} view={View.Home} icon={<HomeIcon />} />
                <NavigationItem label={i18n.calendar} view={View.Calendar} icon={<EventIcon />} />
            </Section>

            {/* trainer section */}
            {(userRole === "trainer") && (
                <Section label={i18n.trainer}>
                    <NavigationItem label={i18n.bookings} view={View.Bookings} icon={<EventAvailableIcon />} />
                </Section>
            )}

            {/* admin section */}
            {(userRole === "admin" || userRole === "owner") && (
                <Section label={i18n.admin}>
                    {clubSetupIsCompleted ? (
                        <>
                            {isCompany ? (
                                <>
                                    {!club?.increasedDataProtections?.includes(IncreasedDataProtections.ACTIVITIES) && (
                                        <NavigationItem label={i18n.activities} view={View.Activities} icon={<ViewListIcon />} />
                                    )}
                                    {!club?.increasedDataProtections?.includes(IncreasedDataProtections.BOOKINGS) && (
                                        <NavigationItem label={i18n.bookings} view={View.Bookings} icon={<EventAvailableIcon />} />
                                    )}
                                    {club?.clubStatus !== "demo" && !club?.increasedDataProtections?.includes(IncreasedDataProtections.DOCUMENTS) && (
                                        <NavigationItem label={i18n.documents} view={View.Documents} icon={<DescriptionIcon />} />
                                    )}
                                    {!club?.increasedDataProtections?.includes(IncreasedDataProtections.MEMEBERS) && (
                                        <NavigationItem label={i18n.members} view={View.Members} icon={<GroupIcon />} />
                                    )}
                                    {!club?.increasedDataProtections?.includes(IncreasedDataProtections.COURTS) && (
                                        <NavigationItem label={i18n.courts} view={View.Courts} icon={<GolfCourseIcon />} />
                                    )}
                                    {!club?.increasedDataProtections?.includes(IncreasedDataProtections.CLUB) && (
                                        <NavigationItem label={(isCompany ? i18n.settingForCompany : i18n.club)} view={View.Club} icon={<SportsTennisIcon />} />
                                    )}
                                    {userRole === "owner" && isCompany && !club?.increasedDataProtections?.includes(IncreasedDataProtections.B2B_FEATURES) && (
                                        <NavigationItem label={i18n.B2BFeatures} view={View.B2BFeatures} icon={<SettingsIcon />} />
                                    )}
                                    <NavigationItem label={i18n.challenge} view={View.Challenge} icon={<ChallengeIcon />} />
                                    <NavigationItem label={i18n.help} onClick={() => window.open(helpB2BLink)} icon={<HelpIcon />} />
                                </>
                            ) : (
                                <>
                                    <NavigationItem label={i18n.activities} view={View.Activities} icon={<ViewListIcon />} />
                                    <NavigationItem label={i18n.bookings} view={View.Bookings} icon={<EventAvailableIcon />} />
                                    {club?.clubStatus !== "demo" && <NavigationItem label={i18n.documents} view={View.Documents} icon={<DescriptionIcon />} />}
                                    <NavigationItem label={i18n.members} view={View.Members} icon={<GroupIcon />} />
                                    <NavigationItem label={i18n.courts} view={View.Courts} icon={<GolfCourseIcon />} />
                                    <NavigationItem label={(isCompany ? i18n.settingForCompany : i18n.club)} view={View.Club} icon={<SportsTennisIcon />} />
                                    <NavigationItem label={i18n.help} onClick={() => window.open(helpLink)} icon={<HelpIcon />} />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <NavigationItem label={i18n.welcome} view={View.Welcome} icon={<EmojiPeopleIcon />} />
                        </>
                    )}
                    {(getPersistedData(PersistedData.DashboardToken)) && (
                        <NavigationItem label={i18n.logoutDashboard} onClick={onLogoutDashboard} icon={<RemoveFromQueueIcon />} />
                    )}
                </Section>
            )}

            {/* account section */}
            <Section label={i18n.account}>
                <NavigationItem label={i18n.userProfile} view={View.Profile} icon={<AccountCircleIcon />} />
                <NavigationItem label={i18n.signOut} onClick={onSignOut} icon={<ExitToAppIcon />} />
            </Section>

            {/* language selector */}
            <LanguageSelect />
        </Drawer>
    );
};

export default Menu;
